import { useEffect, useState } from "react";
import api, { apiError, API } from "../lib/api";
import { PageHeader } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { WhatsAppAutomationSettings } from "../components/WhatsAppAutomationSettings";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { useNavigate } from "react-router-dom";
import { KeyRound, Bot, Send, ScrollText, Gift, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { IncentivePrograms } from "../components/IncentivePrograms";
import { InvoiceSettings } from "../components/InvoiceSettings";
import { DailyReportSettings } from "../components/DailyReportSettings";
import { BranchLocationSettings } from "../components/BranchLocationSettings";
import BookingSite from "./BookingSite";
import { useAuth } from "../context/AuthContext";

const ADMIN_GROUPS = [
  { title: "Accounts & Access", items: [
    { label: "Manager Accounts", desc: "Create accounts & manage permissions (Admin, Manager, Receptionist)", to: "/managers", icon: KeyRound },
  ] },
  { title: "AI & Automation", items: [
    { label: "AI Assistant", desc: "Business AI assistant & booking bot", to: "/ai", icon: Bot },
  ] },
  { title: "WhatsApp & Marketing", items: [
    { label: "WhatsApp Campaigns", desc: "Send bulk WhatsApp campaigns", to: "/campaigns", icon: Send },
  ] },
  { title: "System & Security", items: [
    { label: "Audit Log", desc: "Track every important action", to: "/audit", icon: ScrollText },
  ] },
];

const PROVIDERS = [
  { key: "whatsapp", label: "WhatsApp Business", fields: ["phone_number_id", "access_token", "verify_token"] },
  { key: "google_reviews", label: "Google Business Profile", fields: ["account_id", "api_key"] },
  { key: "meta", label: "Instagram / Facebook", fields: ["page_id", "access_token"] },
  { key: "email", label: "Email", fields: ["from_email", "api_key"] },
  { key: "sms", label: "SMS", fields: ["sender_id", "api_key"] },
];

export default function Settings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canBookingSite = ["OWNER", "SUPER_ADMIN"].includes(user?.role) || !!(user?.permissions || {}).booking_site_manage;
  const [cfg, setCfg] = useState({});
  const [incentive, setIncentive] = useState({ gst_included: false, discount_included: true, products_eligible: false });
  const DISCOUNT_ROLES = ["OWNER", "SUPER_ADMIN", "BRANCH_MANAGER", "ACCOUNTANT", "RECEPTION", "STAFF"];
  const [limits, setLimits] = useState({ OWNER: 100, SUPER_ADMIN: 100, BRANCH_MANAGER: 30, ACCOUNTANT: 20, RECEPTION: 10, STAFF: 0 });
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [refset, setRefset] = useState({ enabled: true, discount_percent: 10, cashback_percent: 10, show_on_invoice: true, show_in_whatsapp: true, cashback_wa_enabled: false, cashback_wa_message: "" });
  const [gst, setGst] = useState({ total: 5, cgst: 2.5, sgst: 2.5 });

  useEffect(() => {
    api.get("/integrations/config").then((r) => setCfg(r.data));
    api.get("/settings/incentive").then((r) => { if (Object.keys(r.data).length) setIncentive(r.data); });
    api.get("/settings/gst").then((r) => { if (r.data && r.data.total != null) setGst({ total: r.data.total, cgst: r.data.cgst ?? r.data.total / 2, sgst: r.data.sgst ?? r.data.total / 2 }); }).catch(() => {});
    api.get("/settings/discount_limits").then((r) => { if (Object.keys(r.data).length) setLimits((l) => ({ ...l, ...r.data })); });
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    api.get("/referrals/settings").then((r) => setRefset((s) => ({ ...s, ...r.data }))).catch(() => {});
  }, []);

  const saveRefset = async () => { try { await api.put("/referrals/settings", { ...refset, discount_percent: Number(refset.discount_percent), cashback_percent: Number(refset.cashback_percent) }); toast.success("Referral settings saved"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  const managers = users.filter((u) => u.role === "BRANCH_MANAGER");
  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";
  const reloadUsers = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  const changeBranch = async (id, branch_id) => {
    try { await api.put(`/users/${id}`, { branch_id }); toast.success("Branch updated"); reloadUsers(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggleStatus = async (u) => {
    const status = u.status === "inactive" ? "active" : "inactive";
    try { await api.put(`/users/${u.id}`, { status }); toast.success(status === "active" ? "Account enabled" : "Account disabled"); reloadUsers(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const resetPassword = async (u) => {
    const pw = window.prompt(`Set a new password for ${u.name} (${u.email}):`);
    if (!pw) return;
    try { await api.put(`/users/${u.id}`, { password: pw }); toast.success("Password reset"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const saveLimits = async () => {
    try { await api.put("/settings/discount_limits", limits); toast.success("Discount limits saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const saveProvider = async (key) => {
    try { await api.put(`/integrations/config/${key}`, cfg[key]); toast.success(`${key} saved`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const saveIncentive = async () => {
    try { await api.put("/settings/incentive", incentive); toast.success("Incentive settings saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const saveGst = async () => {
    const total = Number(gst.total), cgst = Number(gst.cgst), sgst = Number(gst.sgst);
    if ([total, cgst, sgst].some((n) => Number.isNaN(n) || n < 0)) { toast.error("Enter valid GST percentages"); return; }
    if (Math.abs((cgst + sgst) - total) > 0.001) { toast.error("CGST % + SGST % must equal Total GST %"); return; }
    try { await api.put("/settings/gst", { total, cgst, sgst }); toast.success("GST settings saved — applies to all new bills & incentives"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const update = (key, patch) => setCfg((c) => ({ ...c, [key]: { ...c[key], ...patch } }));

  return (
    <div>
      <PageHeader title="Settings" subtitle="Business configuration & integrations" />
      <Tabs defaultValue="admin">
        <TabsList className="bg-secondary">
          <TabsTrigger value="admin" data-testid="tab-admin">Administration</TabsTrigger>
          <TabsTrigger value="incentive" data-testid="tab-incentive">Incentive Rules</TabsTrigger>
          <TabsTrigger value="discounts" data-testid="tab-discounts">Discount Limits</TabsTrigger>
          <TabsTrigger value="gst" data-testid="tab-gst">GST Settings</TabsTrigger>
          <TabsTrigger value="managers" data-testid="tab-managers">Manager Accounts</TabsTrigger>
          <TabsTrigger value="wa-automation" data-testid="tab-wa-automation">WhatsApp Automation</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations">Integrations</TabsTrigger>
          <TabsTrigger value="referral" data-testid="tab-referral">Referral Program</TabsTrigger>
          <TabsTrigger value="invoice" data-testid="tab-invoice-settings">Invoice Settings</TabsTrigger>
          <TabsTrigger value="dailyreport" data-testid="tab-daily-report">Daily Report</TabsTrigger>
          <TabsTrigger value="branchloc" data-testid="tab-branch-location">Branch Locations</TabsTrigger>
          {canBookingSite && <TabsTrigger value="bookingsite" data-testid="tab-booking-site">Booking Home Page</TabsTrigger>}
        </TabsList>

        <TabsContent value="admin" className="mt-4 space-y-6" data-testid="admin-section">
          {ADMIN_GROUPS.map((g) => (
            <div key={g.title}>
              <h3 className="font-display text-lg mb-3">{g.title}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {g.items.map((it) => {
                  const Icon = it.icon;
                  return (
                    <button key={it.to} onClick={() => navigate(it.to)} data-testid={`admin-card-${it.to.replace(/\//g, "")}`}
                      className="text-left rounded-lg border border-border bg-card hover:border-primary p-4 flex items-center gap-3 transition-colors">
                      <span className="h-10 w-10 rounded-md bg-primary/15 text-primary grid place-items-center shrink-0"><Icon size={18} /></span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-medium">{it.label}</span>
                        <span className="block text-xs text-muted-foreground truncate">{it.desc}</span>
                      </span>
                      <ChevronRight size={16} className="text-muted-foreground shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          <div>
            <h3 className="font-display text-lg mb-3">Referral Program</h3>
            <p className="text-xs text-muted-foreground mb-2">Turn the program on/off and set the New Customer Discount % and Referrer Wallet Reward %.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <button onClick={() => document.querySelector('[data-testid="tab-referral"]')?.click()} data-testid="admin-card-referral"
                className="text-left rounded-lg border border-border bg-card hover:border-primary p-4 flex items-center gap-3 transition-colors">
                <span className="h-10 w-10 rounded-md bg-primary/15 text-primary grid place-items-center shrink-0"><Gift size={18} /></span>
                <span className="flex-1 min-w-0">
                  <span className="block font-medium">Referral Program Settings</span>
                  <span className="block text-xs text-muted-foreground truncate">ON/OFF · New Customer Discount % · Referrer Reward %</span>
                </span>
                <ChevronRight size={16} className="text-muted-foreground shrink-0" />
              </button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="incentive" className="mt-4 space-y-6" data-testid="incentive-rules-section">
          <Card className="p-6 bg-card border-border max-w-lg space-y-4">
            <h3 className="font-display text-lg">Incentive Rules</h3>
            <Row label="Include GST in eligible billing"><Switch checked={incentive.gst_included} onCheckedChange={(v) => setIncentive({ ...incentive, gst_included: v })} data-testid="inc-gst" /></Row>
            <Row label="Deduct discounts from eligible billing"><Switch checked={incentive.discount_included} onCheckedChange={(v) => setIncentive({ ...incentive, discount_included: v })} data-testid="inc-discount" /></Row>
            <Row label="Product sales are incentive-eligible"><Switch checked={incentive.products_eligible} onCheckedChange={(v) => setIncentive({ ...incentive, products_eligible: v })} data-testid="inc-products" /></Row>
            <Button onClick={saveIncentive} data-testid="save-incentive" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">Save Incentive Rules</Button>
          </Card>
          <IncentivePrograms />
        </TabsContent>

        <TabsContent value="discounts" className="mt-4">
          <Card className="p-6 bg-card border-border max-w-lg space-y-4">
            <p className="text-sm text-muted-foreground">Maximum discount % each role can apply per service line in billing.</p>
            {DISCOUNT_ROLES.map((role) => (
              <div key={role} className="flex items-center justify-between gap-4">
                <span className="text-sm">{role}</span>
                <div className="flex items-center gap-2">
                  <Input type="number" value={limits[role] ?? 0} data-testid={`limit-${role}`}
                    onChange={(e) => setLimits({ ...limits, [role]: Number(e.target.value) })}
                    className="w-24 bg-secondary" />
                  <span className="text-muted-foreground text-sm">%</span>
                </div>
              </div>
            ))}
            <Button onClick={saveLimits} data-testid="save-limits" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">Save Discount Limits</Button>
          </Card>
        </TabsContent>

        <TabsContent value="gst" className="mt-4" data-testid="gst-settings-section">
          <Card className="p-6 bg-card border-border max-w-lg space-y-4">
            <div>
              <h3 className="font-display text-lg">GST Settings</h3>
              <p className="text-sm text-muted-foreground">Set your GST rates. These apply to <b>all new bills</b> and update automatically when changed. Staff incentives are always calculated on the amount <b>excluding GST</b>.</p>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm">Total GST %</span>
              <div className="flex items-center gap-2">
                <Input type="number" step="0.01" value={gst.total} data-testid="gst-total"
                  onChange={(e) => { const v = e.target.value; const n = Number(v); setGst({ total: v, cgst: Number.isNaN(n) ? gst.cgst : +(n / 2).toFixed(2), sgst: Number.isNaN(n) ? gst.sgst : +(n / 2).toFixed(2) }); }}
                  className="w-28 bg-secondary" />
                <span className="text-muted-foreground text-sm">%</span>
              </div>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm">CGST %</span>
              <div className="flex items-center gap-2">
                <Input type="number" step="0.01" value={gst.cgst} data-testid="gst-cgst"
                  onChange={(e) => setGst({ ...gst, cgst: e.target.value })} className="w-28 bg-secondary" />
                <span className="text-muted-foreground text-sm">%</span>
              </div>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm">SGST %</span>
              <div className="flex items-center gap-2">
                <Input type="number" step="0.01" value={gst.sgst} data-testid="gst-sgst"
                  onChange={(e) => setGst({ ...gst, sgst: e.target.value })} className="w-28 bg-secondary" />
                <span className="text-muted-foreground text-sm">%</span>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">CGST % + SGST % must equal Total GST %. Changing Total auto-splits it equally; adjust CGST/SGST manually if needed.</p>
            <div className="rounded-sm border border-primary/30 bg-primary/5 p-3 text-xs text-muted-foreground">
              Example: Service ₹100 + GST {gst.total || 0}% (₹{((Number(gst.total) || 0)).toFixed(2)}) = Customer pays ₹{(100 + (Number(gst.total) || 0)).toFixed(2)}. Incentive base = <b className="text-primary">₹100</b> (GST excluded).
            </div>
            <Button onClick={saveGst} data-testid="save-gst" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">Save GST Settings</Button>
          </Card>
        </TabsContent>

        <TabsContent value="wa-automation" className="mt-4"><WhatsAppAutomationSettings /></TabsContent>
        <TabsContent value="managers" className="mt-4">
          <Card className="p-6 bg-card border-border" data-testid="managers-panel">
            <h3 className="font-display text-xl mb-1">Branch Manager Accounts</h3>
            <p className="text-sm text-muted-foreground mb-4">Each manager is locked to a single branch. Reset a temporary password, reassign a branch, or disable access. Only Owner/Super Admin can manage these.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-muted-foreground text-left border-b border-border">
                  <th className="py-2">Manager</th><th>Email</th><th>Branch</th><th>Status</th><th className="text-right">Actions</th>
                </tr></thead>
                <tbody>
                  {managers.map((u) => (
                    <tr key={u.id} className="border-b border-border/50" data-testid={`manager-row-${u.id}`}>
                      <td className="py-3">{u.name}</td>
                      <td className="text-muted-foreground">{u.email}</td>
                      <td>
                        <Select value={u.branch_id || ""} onValueChange={(v) => changeBranch(u.id, v)}>
                          <SelectTrigger data-testid={`manager-branch-${u.id}`} className="h-8 w-56 bg-secondary border-border"><SelectValue placeholder="Select branch" /></SelectTrigger>
                          <SelectContent>
                            {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td>
                        <span className={u.status === "inactive" ? "text-destructive" : "text-primary"}>
                          {u.status === "inactive" ? "Disabled" : "Active"}
                        </span>
                      </td>
                      <td className="text-right whitespace-nowrap">
                        <Button size="sm" variant="outline" onClick={() => resetPassword(u)} data-testid={`manager-reset-${u.id}`} className="rounded-sm mr-2">Reset Password</Button>
                        <Button size="sm" variant={u.status === "inactive" ? "default" : "outline"} onClick={() => toggleStatus(u)} data-testid={`manager-toggle-${u.id}`} className="rounded-sm">
                          {u.status === "inactive" ? "Enable" : "Disable"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!managers.length && <tr><td colSpan={5} className="text-center text-muted-foreground py-6">No manager accounts found.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="integrations" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {PROVIDERS.map((p) => (
              <Card key={p.key} className="p-5 bg-card border-border" data-testid={`integration-${p.key}`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display text-xl">{p.label}</h3>
                  <Switch checked={cfg[p.key]?.enabled || false} onCheckedChange={(v) => update(p.key, { enabled: v })} data-testid={`toggle-${p.key}`} />
                </div>
                <div className="space-y-2">
                  {p.fields.map((f) => (
                    <div key={f}>
                      <Label className="text-xs capitalize">{f.replace(/_/g, " ")}</Label>
                      <Input value={cfg[p.key]?.config?.[f] || ""} onChange={(e) => update(p.key, { config: { ...cfg[p.key]?.config, [f]: e.target.value } })} className="bg-secondary mt-1" />
                    </div>
                  ))}
                </div>
                <Button size="sm" onClick={() => saveProvider(p.key)} data-testid={`save-${p.key}`} className="mt-4 rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">Save</Button>
              </Card>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4">Webhook URL for WhatsApp: <code className="text-primary">{API}/wa-ai/webhook</code></p>
        </TabsContent>

        <TabsContent value="referral" className="mt-4">
          <div className="max-w-xl">
            <Card className="p-6 bg-card border-border space-y-4" data-testid="referral-settings-panel">
              <h3 className="font-display text-xl">Referral Program</h3>
              <Row label="Referral program enabled"><Switch checked={refset.enabled} onCheckedChange={(v) => setRefset({ ...refset, enabled: v })} data-testid="ref-enabled" /></Row>
              <div className="flex items-center justify-between gap-4"><span className="text-sm">Referral discount % (referred customer)</span><Input type="number" value={refset.discount_percent} onChange={(e) => setRefset({ ...refset, discount_percent: e.target.value })} className="w-24 bg-secondary" data-testid="ref-discount" /></div>
              <div className="flex items-center justify-between gap-4"><span className="text-sm">Referrer wallet cashback %</span><Input type="number" value={refset.cashback_percent} onChange={(e) => setRefset({ ...refset, cashback_percent: e.target.value })} className="w-24 bg-secondary" data-testid="ref-cashback" /></div>
              <Row label="Show referral code on invoice"><Switch checked={refset.show_on_invoice} onCheckedChange={(v) => setRefset({ ...refset, show_on_invoice: v })} data-testid="ref-show-invoice" /></Row>
              <Row label="Show referral code in WhatsApp"><Switch checked={refset.show_in_whatsapp} onCheckedChange={(v) => setRefset({ ...refset, show_in_whatsapp: v })} data-testid="ref-show-whatsapp" /></Row>
              <p className="text-[11px] text-muted-foreground">Every completed bill generates a new unique referral code. Percentages apply to future bills — change anytime. What is shown with the invoice is now controlled in the <b>Invoice Settings</b> tab.</p>

              <div className="border-t border-border pt-4 space-y-3">
                <h4 className="font-display text-lg">Referral Cashback WhatsApp Notification</h4>
                <Row label="Send Referral Cashback WhatsApp Notification"><Switch checked={refset.cashback_wa_enabled} onCheckedChange={(v) => setRefset({ ...refset, cashback_wa_enabled: v })} data-testid="ref-cashback-wa-enabled" /></Row>
                <div>
                  <Label className="text-xs">Referral Cashback Message</Label>
                  <Textarea value={refset.cashback_wa_message} onChange={(e) => setRefset({ ...refset, cashback_wa_message: e.target.value })} rows={4} className="bg-secondary mt-1" placeholder="🎉 Great News! ..." data-testid="ref-cashback-wa-message" />
                  <p className="text-[11px] text-muted-foreground mt-1">Placeholders: <code>{"{{customer_name}}"}</code> (referred customer), <code>{"{{cashback_amount}}"}</code> (wallet cashback). Sent once to the referrer after the referred bill is paid and cashback is credited.</p>
                </div>
              </div>

              <Button onClick={saveRefset} data-testid="save-referral" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">Save Referral Settings</Button>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="invoice" className="mt-4" data-testid="invoice-settings-section">
          <InvoiceSettings />
        </TabsContent>

        <TabsContent value="dailyreport" className="mt-4" data-testid="daily-report-section">
          <DailyReportSettings />
        </TabsContent>

        <TabsContent value="branchloc" className="mt-4" data-testid="branch-location-section">
          <BranchLocationSettings />
        </TabsContent>

        {canBookingSite && (
          <TabsContent value="bookingsite" className="mt-4" data-testid="booking-site-section">
            <BookingSite embedded />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function Row({ label, children }) {
  return <div className="flex items-center justify-between"><span className="text-sm">{label}</span>{children}</div>;
}
